import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { BookOpen, Loader2, Sparkles, Clock, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export const BlogGeneratorPage = () => {
  const { api } = useAuth();
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState('Professional');
  const [brandPersonality, setBrandPersonality] = useState('');
  const [wordCount, setWordCount] = useState(900);
  const [keywords, setKeywords] = useState('');
  const [cta, setCta] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!topic.trim() || !audience.trim()) {
      toast.error('Topic and Audience are required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/generate/blog', {
        topic: topic.trim(),
        audience: audience.trim(),
        tone,
        brand_personality: brandPersonality.trim() || undefined,
        word_count: Number(wordCount),
        keywords: keywords.trim() || undefined,
        cta: cta.trim() || undefined,
      });
      setResult(res.data);
      toast.success('Blog post generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const cj = result?.content_json || {};
  const fullMarkdown = () => {
    const parts = [
      `# ${cj.title || ''}`,
      cj.meta_description ? `> ${cj.meta_description}` : '',
      cj.intro || '',
      ...(cj.sections || []).map((s) => `## ${s.heading}\n\n${s.body}`),
      cj.conclusion || '',
      cj.cta ? `**${cj.cta}**` : '',
    ];
    return parts.filter(Boolean).join('\n\n');
  };

  const copy = () => {
    navigator.clipboard.writeText(fullMarkdown());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6" data-testid="blog-generator-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-indigo-600" />
          AI Blog Writer
        </h1>
        <p className="text-zinc-500 text-sm mt-0.5">
          Generate long-form articles with SEO metadata, structured sections, and a CTA.
        </p>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <Card className="border-zinc-200 h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Blog details</CardTitle>
            <CardDescription>AI will match your brand's voice and tone.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="blog-topic">Topic *</Label>
              <Input id="blog-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="How to scale an e-commerce store" data-testid="blog-topic-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blog-audience">Audience *</Label>
              <Input id="blog-audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="DTC founders, marketing leads" data-testid="blog-audience-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blog-tone">Tone</Label>
              <Input id="blog-tone" value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Professional" data-testid="blog-tone-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blog-brand">Brand personality</Label>
              <Input id="blog-brand" value={brandPersonality} onChange={(e) => setBrandPersonality(e.target.value)} placeholder="confident, witty, data-driven" data-testid="blog-brand-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blog-words">Target length (words)</Label>
              <Input id="blog-words" type="number" min={300} max={3000} step={100} value={wordCount} onChange={(e) => setWordCount(e.target.value)} data-testid="blog-wordcount-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blog-keywords">SEO keywords</Label>
              <Input id="blog-keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="ecommerce growth, conversion rate" data-testid="blog-keywords-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blog-cta">Call-to-Action</Label>
              <Input id="blog-cta" value={cta} onChange={(e) => setCta(e.target.value)} placeholder="Book a free strategy call" data-testid="blog-cta-input" />
            </div>
            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={handleGenerate}
              disabled={loading}
              data-testid="blog-generate-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate article
            </Button>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 min-h-[420px]">
          <CardContent className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
                <p className="text-sm text-zinc-500">Crafting your article...</p>
              </div>
            ) : !result ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <BookOpen className="w-12 h-12 text-zinc-200 mb-3" />
                <p className="text-sm text-zinc-500">Your article will appear here.</p>
              </div>
            ) : (
              <div data-testid="blog-result">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    {cj.reading_time_minutes && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {cj.reading_time_minutes} min read
                      </Badge>
                    )}
                    {cj.slug && <Badge variant="outline" className="font-mono text-[10px]">/{cj.slug}</Badge>}
                  </div>
                  <Button size="sm" variant="outline" onClick={copy} data-testid="blog-copy-btn">
                    {copied ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                    {copied ? 'Copied' : 'Copy all'}
                  </Button>
                </div>
                <ScrollArea className="h-[560px] pr-3">
                  <article className="prose prose-zinc max-w-none">
                    <h1 className="!text-2xl !mt-0">{cj.title}</h1>
                    {cj.meta_description && (
                      <p className="text-zinc-500 italic border-l-2 border-indigo-300 pl-3">{cj.meta_description}</p>
                    )}
                    {cj.intro && <p>{cj.intro}</p>}
                    {(cj.sections || []).map((s, i) => (
                      <div key={i}>
                        <h2 className="!text-lg">{s.heading}</h2>
                        <p className="whitespace-pre-wrap">{s.body}</p>
                      </div>
                    ))}
                    {cj.conclusion && (
                      <>
                        <h2 className="!text-lg">Conclusion</h2>
                        <p className="whitespace-pre-wrap">{cj.conclusion}</p>
                      </>
                    )}
                    {cj.cta && (
                      <div className="mt-4 p-4 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-900 font-medium">
                        👉 {cj.cta}
                      </div>
                    )}
                  </article>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
